import { z } from 'zod';
import { defineForm } from '../registry.js';
import { CONFIRMERS, idString, longtext } from './common.js';

// 第2表（居宅サービス計画書(2)）。ニーズ → 長期目標 → 短期目標 → サービス の 4 段の木。
// 旧: CarePlanElementVersion を parentId でつないだ平らな版の集まり。ここでは本文の中で入れ子の配列にする（ID は保つ）。
// 旧の要素ごとの version は帳票の版（document.version）に吸収する。
// 旧も今も本文は文章だけ（期間・サービス種別・頻度・事業者・保険給付対象は未対応）。
// 文章の上限は旧 API に無い（エージェント 500、変更連鎖 1500）。ここは API 側に合わせ上限なし。
// 根拠 ID は旧 API で要素ごとに最大 20 件

export const CAREPLAN_ELEMENT_TYPES = ['need', 'ltGoal', 'stGoal', 'service'] as const;
export type CarePlanElementType = (typeof CAREPLAN_ELEMENT_TYPES)[number];

const evidence = z.array(idString).max(20).optional().describe('根拠の記録ID');

export const serviceSchema = z
  .object({
    serviceId: idString.describe('サービスID'),
    text: longtext('サービス内容').min(1),
    evidenceEventIds: evidence,
  })
  .strict();

export const stGoalSchema = z
  .object({
    stGoalId: idString.describe('短期目標ID'),
    text: longtext('短期目標').min(1),
    evidenceEventIds: evidence,
    services: z.array(serviceSchema).default([]).describe('サービス'),
  })
  .strict();

export const ltGoalSchema = z
  .object({
    ltGoalId: idString.describe('長期目標ID'),
    text: longtext('長期目標').min(1),
    evidenceEventIds: evidence,
    stGoals: z.array(stGoalSchema).default([]).describe('短期目標'),
  })
  .strict();

export const needSchema = z
  .object({
    needId: idString.describe('ニーズID'),
    text: longtext('生活全般の解決すべき課題（ニーズ）').min(1),
    evidenceEventIds: evidence,
    ltGoals: z.array(ltGoalSchema).default([]).describe('長期目標'),
  })
  .strict();

// 旧の変更連鎖（change-chain.ts validatePlan）と同じく、木全体で ID の重複を許さない
export const careplan2Schema = z
  .object({
    needs: z.array(needSchema).min(1).describe('ニーズ'),
  })
  .strict()
  .superRefine((v, ctx) => {
    const seen = new Set<string>();
    const check = (id: string, path: (string | number)[]) => {
      if (seen.has(id)) ctx.addIssue({ code: 'custom', path, message: `要素IDが重複しています: ${id}` });
      seen.add(id);
    };
    v.needs.forEach((n, i) => {
      check(n.needId, ['needs', i, 'needId']);
      n.ltGoals.forEach((l, j) => {
        check(l.ltGoalId, ['needs', i, 'ltGoals', j, 'ltGoalId']);
        l.stGoals.forEach((s, k) => {
          check(s.stGoalId, ['needs', i, 'ltGoals', j, 'stGoals', k, 'stGoalId']);
          s.services.forEach((sv, m) => check(sv.serviceId, ['needs', i, 'ltGoals', j, 'stGoals', k, 'services', m, 'serviceId']));
        });
      });
    });
  });
export type Careplan2Body = z.infer<typeof careplan2Schema>;

export const careplan2Form = defineForm({
  app: 'caremanager',
  docType: 'careplan2',
  name: '居宅サービス計画書(2)（第2表）',
  version: 1,
  schema: careplan2Schema,
  confirmers: CONFIRMERS,
  description: 'ニーズ・長期目標・短期目標・サービスの木。第3表とモニタリングがここの ID を参照する',
});
